import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import LocalFireDepartmentOutlinedIcon from "@mui/icons-material/LocalFireDepartmentOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link as RouterLink, useParams } from "react-router-dom";

import { useDataStore } from "../dataStore";
import { toMediaUrl, withImageSize } from "../api";
import ImageGalleryModal from "../components/ImageGalleryModal";
import BrandedImageFallback from "../components/BrandedImageFallback";
import PageStateCard from "../components/PageStateCard";
import { brandThemeCssVars } from "../themeTokens";

function parseSpecSections(spec) {
  if (!spec || typeof spec !== "string") return [];

  const parts = spec
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean);

  return parts.map((block) => {
    const lines = block.split("\n");
    const title = (lines.shift() || "").trim();
    const body = lines.join("\n").trim();

    return { title, body };
  });
}

export default function ProductDetails() {
  const { slug } = useParams();
  const {
    loading,
    error,
    getProductBySlug,
    getCategoryName,
    getProductAllergens,
    brandTheme,
    t,
    retryCore,
  } = useDataStore();
  const c = brandTheme;
  const p = getProductBySlug(slug);

  const images = useMemo(() => {
    const imgs = p?.productImage_product || [];

    return imgs.map((x) => ({
      id: x.iref,
      url: toMediaUrl(x.image),
      isMain: x.isMain,
      name: x.name,
    }));
  }, [p]);

  const mainIndex = useMemo(() => {
    if (!images.length) return 0;

    const idx = images.findIndex((x) => x.isMain);

    return idx >= 0 ? idx : 0;
  }, [images]);

  const mainImg = images[mainIndex]?.url || "";

  const sections = useMemo(() => parseSpecSections(p?.spec), [p]);
  const productAllergens = useMemo(() => getProductAllergens(p), [getProductAllergens, p]);

  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const openGalleryAt = (idx) => {
    setGalleryIndex(Math.max(0, Math.min(idx, images.length - 1)));
    setGalleryOpen(true);
  };

  if (loading) {
    return (
      <PageStateCard
        loading
        title={t("common.loading")}
        brandTheme={c}
      />
    );
  }

  if (error) {
    return (
      <PageStateCard
        tone="error"
        title={t("common.loadErrorTitle")}
        description={t("common.loadError")}
        actionLabel={t("common.retry")}
        onAction={retryCore}
        brandTheme={c}
      />
    );
  }

  if (!p) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: "18px",
          border: "1px solid rgba(124,45,18,0.10)",
          bgcolor: c.surface,
        }}
      >
        <CardContent sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 900, color: c.text }}>
            {t("products.notFoundTitle")}
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.75 }}>
            {t("products.notFoundDesc")}
          </Typography>

          <Button
            component={RouterLink}
            to="/products"
            variant="contained"
            sx={{
              mt: 2,
              borderRadius: "12px",
              fontWeight: 900,
              textTransform: "none",
            }}
          >
            {t("common.backToProducts")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const catName = p.pCategory ? getCategoryName(p.pCategory) : "";

  return (
    <Stack
      spacing={{ xs: 2, md: 2.5 }}
      sx={{
        ...brandThemeCssVars(c),
        width: "100%",
        maxWidth: 1180,
        mx: "auto",
      }}
    >
      {/* Back row */}
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <IconButton
          component={RouterLink}
          to="/products"
          aria-label={t("common.backToProducts")}
          sx={{
            width: 44,
            height: 44,
            bgcolor: c.background,
            border: "1px solid rgba(124,45,18,0.10)",
            color: c.accent,
            "&:hover": {
              bgcolor: c.surface,
            },
          }}
        >
          <ArrowBackRoundedIcon />
        </IconButton>

        <Typography sx={{ fontWeight: 800, color: c.accent }}>
          {t("common.backToProducts")}
        </Typography>
      </Stack>

      {/* Hero image */}
      <Card
        elevation={0}
        sx={{
          overflow: "hidden",
          borderRadius: { xs: "18px", md: "24px" },
          border: "1px solid rgba(124,45,18,0.10)",
          boxShadow: "0 24px 64px rgba(33,20,12,0.16)",
          bgcolor: c.surface,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.08fr) minmax(360px, 0.92fr)" },
          }}
        >
          <Box sx={{ position: "relative", minHeight: { xs: 300, sm: 400, md: 440, lg: 560 } }}>
            <Box
              component={images.length ? "button" : "div"}
              type={images.length ? "button" : undefined}
              onClick={() => images.length ? openGalleryAt(mainIndex) : null}
              aria-label={
                images.length
                  ? `${t("common.image")}: ${p.name}`
                  : undefined
              }
              sx={{
                cursor: images.length ? "pointer" : "default",
                position: { xs: "relative", lg: "absolute" },
                inset: { lg: 0 },
                height: { xs: 300, sm: 400, md: 440, lg: "100%" },
                width: "100%",
                p: 0,
                border: 0,
                textAlign: "inherit",
                bgcolor: c.background,
                "&:focus-visible": {
                  outline: `4px solid ${c.primary}`,
                  outlineOffset: -4,
                },
              }}
            >
              {mainImg ? (
                <img
                  src={withImageSize(mainImg, "l")}
                  alt={p.name}
                  loading="eager"
                  decoding="async"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <BrandedImageFallback
                  icon={<RestaurantMenuRoundedIcon />}
                  label={t("common.noImage")}
                  brandTheme={c}
                />
              )}
            </Box>

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(180deg, rgba(17,17,17,0.02) 34%, rgba(17,17,17,0.50) 100%)",
                pointerEvents: "none",
              }}
            />

            <Stack
              direction="row"
              spacing={1}
              sx={{
                position: "absolute",
                left: 14,
                top: 14,
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {catName ? (
                <Chip
                  icon={<RestaurantMenuRoundedIcon />}
                  label={catName}
                  sx={{
                    fontWeight: 900,
                    color: c.text,
                    bgcolor: c.surface,
                    border: "1px solid rgba(255,255,255,0.36)",
                    "& .MuiChip-icon": { color: c.accent },
                  }}
                />
              ) : null}

              {p.isNew ? (
                <Chip
                  label={t("products.new")}
                  sx={{
                    fontWeight: 900,
                    color: c.onPrimary,
                    bgcolor: c.primary,
                    border: "1px solid rgba(17,17,17,0.08)",
                  }}
                />
              ) : null}
            </Stack>

          </Box>

          <CardContent sx={{ p: { xs: 2.25, sm: 3, lg: 4 } }}>
            <Stack spacing={2} sx={{ height: "100%", justifyContent: "center" }}>
              <Box>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: 0,
                    lineHeight: 1.03,
                    color: c.text,
                    fontSize: { xs: 32, sm: 42, md: 48 },
                  }}
                >
                  {p.name}
                </Typography>

                {p.shortName && p.shortName !== p.name ? (
                  <Typography
                    sx={{
                      mt: 1,
                      color: c.accent,
                      fontWeight: 800,
                      lineHeight: 1.45,
                    }}
                  >
                    {p.shortName}
                  </Typography>
                ) : null}
              </Box>

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "wrap",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                {typeof p.kcal === "number" ? (
                  <Chip
                    icon={<LocalFireDepartmentOutlinedIcon />}
                    label={`${p.kcal} kcal`}
                    sx={{
                      fontWeight: 850,
                      bgcolor: c.background,
                      border: "1px solid rgba(124,45,18,0.10)",
                      "& .MuiChip-icon": { color: c.accent },
                    }}
                  />
                ) : null}

                {p.isDeliverable ? (
                  <Chip
                    icon={<LocalShippingOutlinedIcon />}
                    label={t("products.deliverable")}
                    sx={{
                      fontWeight: 850,
                      bgcolor: c.surface,
                      border: "1px solid rgba(124,45,18,0.12)",
                      "& .MuiChip-icon": { color: c.accent },
                    }}
                  />
                ) : null}
              </Stack>

              {p.description ? (
                <Typography
                  color="text.secondary"
                  sx={{
                    whiteSpace: "pre-line",
                    lineHeight: 1.75,
                    fontSize: { xs: 15.5, md: 16 },
                  }}
                >
                  {p.description}
                </Typography>
              ) : null}

              {productAllergens.length ? (
                <Box
                  component="section"
                  aria-labelledby="product-allergens-title"
                  sx={{ pt: 0.5 }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoOutlinedIcon
                      fontSize="small"
                      sx={{ color: c.accent }}
                    />
                    <Typography
                      component="h2"
                      id="product-allergens-title"
                      sx={{ color: c.text, fontWeight: 900, fontSize: 16 }}
                    >
                      {t("products.allergens")}
                    </Typography>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mt: 1 }}
                  >
                    {productAllergens.map((allergen) => {
                      const allergenName =
                        allergen.name || t("products.allergens");

                      return (
                        <Stack
                          key={allergen.iref}
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{
                            minHeight: 40,
                            px: 1.1,
                            py: 0.55,
                            borderRadius: "12px",
                            bgcolor: c.background,
                            border: "1px solid rgba(124,45,18,0.10)",
                          }}
                        >
                          {allergen.iconUrl ? (
                            <Box
                              component="img"
                              src={allergen.iconUrl}
                              alt=""
                              sx={{
                                width: 25,
                                height: 25,
                                flexShrink: 0,
                                objectFit: "contain",
                              }}
                            />
                          ) : null}
                          <Typography
                            variant="body2"
                            sx={{ color: c.text, fontWeight: 800 }}
                          >
                            {allergenName}
                          </Typography>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}

              {images.length > 1 ? (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    pt: 0.5,
                    overflowX: "auto",
                    pb: 0.5,
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  }}
                >
                  {images.map((im, idx) => (
                    <Box
                      key={im.id}
                      component="button"
                      type="button"
                      onClick={() => openGalleryAt(idx)}
                      aria-label={`${t("common.image")} ${idx + 1}: ${im.name || p.name}`}
                      sx={{
                        cursor: "pointer",
                        width: 86,
                        height: 64,
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: "2px solid",
                        borderColor:
                          idx === mainIndex
                            ? c.primary
                            : "rgba(124,45,18,0.10)",
                        bgcolor: c.background,
                        flexShrink: 0,
                        p: 0,
                        transition: "transform 140ms ease, border-color 140ms ease",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          borderColor: "rgba(234,88,12,0.45)",
                        },
                        "&:focus-visible": {
                          outline: `3px solid ${c.primary}`,
                          outlineOffset: 2,
                        },
                        "@media (prefers-reduced-motion: reduce)": {
                          transition: "none",
                          "&:hover": { transform: "none" },
                        },
                      }}
                    >
                      <img
                        src={withImageSize(im.url, "s")}
                        alt={im.name || p.name}
                        loading="lazy"
                        decoding="async"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </CardContent>
        </Box>

      </Card>

      {/* Details sections */}
      {sections.length ? (
        <Stack spacing={1.25}>
          {sections.map((s) => (
            <Accordion
              key={s.title}
              elevation={0}
              sx={{
                borderRadius: "18px !important",
                border: "1px solid rgba(124,45,18,0.10)",
                overflow: "hidden",
                bgcolor: c.surface,
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 900, color: c.text }}>
                  {s.title}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Typography
                  color="text.secondary"
                  sx={{ whiteSpace: "pre-line", lineHeight: 1.7 }}
                >
                  {s.body}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : null}

      <Divider sx={{ my: 1, borderColor: "rgba(124,45,18,0.10)" }} />

      <ImageGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        title={p.name}
        images={images}
        index={galleryIndex}
        setIndex={setGalleryIndex}
        t={t}
      />
    </Stack>
  );
}
